import React, { useEffect, useRef, useState } from 'react'
import { TextField, TextFieldProps, InputAdornment } from '@mui/material'
import { useNumberFormat } from '../../hooks/useNumberFormat'
import {
  decimalSeparator,
  formatNumber,
  parseLocaleNumber,
  roundTo,
  toNumber,
} from '../../utils/formatNumbers'
import './NumberInput.css'

type NumberDraftOptions = {
  value: number | string | null | undefined
  onChange: (value: number | null) => void
  decimals?: number
  min?: number
  max?: number
}

/** Plain text for editing: no grouping, locale decimal separator. */
function editText(value: number | null | undefined, locale: string, decimals: number) {
  if (value === null || value === undefined || !Number.isFinite(value)) return ''
  return String(roundTo(value, decimals)).replace('.', decimalSeparator(locale))
}

/**
 * Shared behaviour of number fields: raw number in, raw number out,
 * grouped/fixed decimals when not focused, free typing while focused.
 */
export function useNumberDraft({ value: rawValue, onChange, decimals = 2, min, max }: NumberDraftOptions) {
  const value =
    typeof rawValue === 'string' ? toNumber(rawValue) : rawValue
  const { locale } = useNumberFormat()
  const sep = decimalSeparator(locale)
  const [focused, setFocused] = useState(false)
  const [draft, setDraft] = useState(() => editText(value, locale, decimals))
  const lastEmitted = useRef<number | null | undefined>(value)

  useEffect(() => {
    if (!focused && value !== lastEmitted.current) {
      setDraft(editText(value, locale, decimals))
      lastEmitted.current = value
    }
  }, [value, focused, locale, decimals])

  const emit = (next: number | null) => {
    lastEmitted.current = next
    onChange(next)
  }

  const accept = (raw: string) => {
    let text = raw.replace(/[\s\u00A0\u202F]/g, '')
    const allowNegative = min === undefined || min < 0
    if (text === '' || (allowNegative && text === '-')) {
      setDraft(text)
      emit(null)
      return
    }
    text = text.replace(/[.,]/g, decimals > 0 ? sep : '')
    const pattern = new RegExp(
      `^${allowNegative ? '-?' : ''}\\d*${decimals > 0 ? `(\\${sep}\\d{0,${decimals}})?` : ''}$`
    )
    if (!pattern.test(text)) return
    setDraft(text)
    emit(parseLocaleNumber(text.replace(sep, '.'), 'en-US'))
  }

  const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const parsed = parseLocaleNumber(e.clipboardData.getData('text'), locale)
    e.preventDefault()
    if (parsed === null) return
    const next = roundTo(parsed, decimals)
    setDraft(editText(next, locale, decimals))
    emit(next)
  }

  const handleBlur = () => {
    setFocused(false)
    if (value === null || value === undefined) return
    let next = roundTo(value, decimals)
    if (min !== undefined && next < min) next = min
    if (max !== undefined && next > max) next = max
    if (next !== value) emit(next)
    setDraft(editText(next, locale, decimals))
  }

  const display =
    focused || value === null || value === undefined || !Number.isFinite(value)
      ? draft
      : formatNumber(value, { locale, decimals })

  return {
    inputProps: {
      type: 'text' as const,
      inputMode: (decimals > 0 ? 'decimal' : 'numeric') as 'decimal' | 'numeric',
      autoComplete: 'off',
      value: display,
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => accept(e.target.value),
      onFocus: () => {
        setDraft(editText(value, locale, decimals))
        setFocused(true)
      },
      onBlur: handleBlur,
      onPaste: handlePaste,
    },
  }
}

type NumberInputProps = NumberDraftOptions & {
  /** Show the organisation currency symbol, or a custom suffix such as "%". */
  currency?: boolean
  adornment?: React.ReactNode
  className?: string
  id?: string
  name?: string
  placeholder?: string
  disabled?: boolean
  required?: boolean
  autoFocus?: boolean
  'aria-label'?: string
  'aria-invalid'?: boolean
}

/** Number field styled like the INCYIA form inputs. */
export default function NumberInput({
  value,
  onChange,
  decimals = 2,
  min,
  max,
  currency,
  adornment,
  className = 'incyia-form-inp',
  ...rest
}: NumberInputProps) {
  const { symbol } = useNumberFormat()
  const { inputProps } = useNumberDraft({ value, onChange, decimals, min, max })
  const suffix = adornment ?? (currency ? symbol : null)

  return (
    <div className={`incyia-number-input${suffix ? ' has-suffix' : ''}`}>
      <input {...rest} {...inputProps} className={`${className} incyia-number-input-field`} />
      {suffix ? (
        <span className="incyia-number-input-suffix" aria-hidden>
          {suffix}
        </span>
      ) : null}
    </div>
  )
}

type NumberTextFieldProps = NumberDraftOptions &
  Omit<TextFieldProps, 'value' | 'onChange' | 'type'> & {
    currency?: boolean
    adornment?: React.ReactNode
  }

/** Same behaviour for MUI TextField based forms. */
export function NumberTextField({
  value,
  onChange,
  decimals = 2,
  min,
  max,
  currency,
  adornment,
  InputProps,
  ...rest
}: NumberTextFieldProps) {
  const { symbol } = useNumberFormat()
  const { inputProps } = useNumberDraft({ value, onChange, decimals, min, max })
  const suffix = adornment ?? (currency ? symbol : null)
  const { value: shown, onChange: change, onFocus, onBlur, onPaste, ...nativeProps } = inputProps

  return (
    <TextField
      {...rest}
      value={shown}
      onChange={change as any}
      onFocus={onFocus}
      onBlur={onBlur}
      inputProps={{ ...nativeProps, onPaste, style: { textAlign: 'right' } }}
      InputProps={{
        ...InputProps,
        endAdornment: suffix ? <InputAdornment position="end">{suffix}</InputAdornment> : InputProps?.endAdornment,
      }}
    />
  )
}
